import * as React from 'react';
import * as autoBind from 'react-autobind';
import * as classNames from 'classnames';
import SmartplayerReviewVideoContainer from '../SmartPlayer/Review/SmartplayerReviewVideoContainer';
import ImageContainer from '../Image/ImageContainer';
import FeedbackContainer from '../Feedback/FeedbackContainer';
import ConversationContainer from '../Conversation/ConversationContainer';
import Header from '../Header/Header';
import ReviewService from '../../services/ReviewService/ReviewService';
import ContentTypeParser from '../../utils/ContentTypeParser/ContentTypeParser';
import UserMessaging from '../../utils/UserMessaging';
import Strings from '../../globals/Strings';
import Constants, {AnnotationModes, ModalStates, UserTypes} from '../../globals/Constants';
import ToolbarContainer from '../Toolbar/ToolbarContainer';
import ReviewModel from '../../types/ReviewModel';
import IVideoPlayer from '../../types/IVideoPlayer';
import LocalStorageWrapper from '../../services/LocalStorageWrapper/LocalStorageWrapper';
import DashboardButtonContainer from '../DashboardButton/DashboardButtonContainer';
import UserModel from '../../types/UserModel';
import Hotkey from '../Hotkey/HotKey';
import SettingsAndAccountWidget from '../SettingsAndAccountWidget/SettingsAndAccountWidget';
import ShareReviewButtonContainer from '../ShareReviewButton/ShareReviewButtonContainer';
import ReviewVersionModel from '../../types/ReviewVersionModel';
import VersionsButtonContainer from '../VersionsButton/VersionsButtonContainer';
import {ControlButton, TscButtonType, AlertModal} from '@techsmith/tsc-cloud-style-guide';
import ReviewRouterService from '../../services/ReviewRouterService/ReviewRouterService';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Config from '../../globals/Config';
import Themeable from '../../types/Themeable';
import NotificationsClientPluginWrapperContainer from '../NotificationsClientPluginWrapper/NotificationsClientPluginWrapperContainer';
import {RouteComponentProps} from 'react-router';

export default class Review extends React.Component<ReviewProps, ReviewState> {
   readonly state: ReviewState = {deletingVersion: false, showAlert: false, versionToDelete: null, newAnnotation: ''};
   addCommentLocalStorageKey: string;

   constructor(props: ReviewProps) {
      super(props);
      autoBind(this);

      this.addCommentLocalStorageKey = LocalStorageWrapper.getKey(Constants.localStorageKeys.addComment, props.review.Id, props.currentUser.userId);
   }

   getIsVideo(version: ReviewVersionModel): boolean {
      return ContentTypeParser.getContentTypeIsVideo(version.MediaContentType);
   }

   async addComment(text: string) {
      const {reviewVersion, currentTime, setAnnotationMode, setSelectedComment} = this.props;

      try {
         const comment = await ReviewService.addComment(reviewVersion.Id, {annotation: this.state.newAnnotation, time: currentTime, text: text});
         setAnnotationMode(AnnotationModes.none);
         setSelectedComment(comment.id);
         this.setState({newAnnotation: ''});
      } catch {
         UserMessaging.warn(Strings.errors.failedSaveComment);
         throw new Error(Strings.errors.failedSaveComment);
      }
   }

   storeAnnotation(annotation: string): void {
      if (this.props.annotationMode === AnnotationModes.edit && annotation) {
         this.setState({newAnnotation: JSON.stringify(annotation)});
      } else {
         this.setState({newAnnotation: ''});
      }
   }

   addSilentComment(): void {
      this.addComment('📌');
   }

   goToLatestVersion(): void {
      const {review, history} = this.props;

      history.push(ReviewRouterService.createCanonicalReviewUrl(review.Id, review.ReviewVersions.filter(ver => !ver.IsDeleted).pop().VersionNumber));
   }

   getLatestVersion(): number {
      return this.props.review.ReviewVersions.filter(ver => !ver.IsDeleted && ver.VersionNumber !== this.state.versionToDelete.VersionNumber).pop().VersionNumber;
   }

   deleteVersion(version: ReviewVersionModel): void {
      this.setState({versionToDelete: version, showAlert: true});
   }

   async onPerformDeletion(e: any): Promise<void> {
      const {versionToDelete, deletingVersion} = this.state;
      e.stopPropagation();

      if (!deletingVersion) {
         const {review, reviewVersion, history} = this.props;
         this.setState({deletingVersion: true});

         try {
            await ReviewService.deleteVersion(review.Id, versionToDelete.VersionNumber);
            const versionNumberToLoad = versionToDelete.VersionNumber === reviewVersion.VersionNumber ? this.getLatestVersion() : reviewVersion.VersionNumber;
            history.push(ReviewRouterService.createCanonicalReviewUrl(review.Id, versionNumberToLoad));
         } catch {
            UserMessaging.warn(Strings.errors.failedDeleteVersion);
         }

         this.setState({showAlert: false, deletingVersion: false, versionToDelete: null});
      }
   }

   onCancelDeletion(e: any): void {
      e.stopPropagation();
      this.setState({showAlert: false});
   }

   render() {
      const {review, isCommentHotkeyEnabled, annotationMode, reviewVersion, versionLoading, setModalState, themeName, currentUser, setSelectedComment, hasOwnerPermissions} = this.props;
      const {deletingVersion, showAlert, newAnnotation} = this.state;
      const isVideo = this.getIsVideo(reviewVersion);
      const alertContentClasses = deletingVersion ? 'alert-body' : 'alert';
      const alertContent =
         <div className={alertContentClasses}>
            {deletingVersion && <LoadingSpinner />}
            {!deletingVersion && Strings.labels.versions.confirmVersionDeletionText}
         </div>;
      const reviewContainerClassName = classNames('review-container', 'page');
      const displayedTitle = Config.conversationVideoTitle ? (Config.conversationVideoTitle + ' - ' + review.Title) : review.Title;

      const headerLeft = (
         <>
            <DashboardButtonContainer />
            {hasOwnerPermissions && reviewVersion.VersionNumber === review.ReviewVersions.filter(ver => !ver.IsDeleted).pop().VersionNumber ?
               <div className="review-title-button" role="heading" aria-level={1}>
                  <ControlButton buttonType={TscButtonType.tertiary}
                     title={Strings.titles.editReviewTitle}
                     label={displayedTitle}
                     onClick={() => setModalState(ModalStates.editReviewTitle)}
                     themeName={themeName}
                     testId="edit-review-title-button" />
               </div> :
               <div className="review-title-button default-cursor" role="heading" aria-level={1}>
                  <ControlButton buttonType={TscButtonType.floating}
                     label={displayedTitle}
                     themeName={themeName}
                     testId="edit-review-title" />
               </div>
            }
            <VersionsButtonContainer deleteVersion={this.deleteVersion} />
            {review.ReviewVersions.filter(ver => !ver.IsDeleted).pop().VersionNumber !== reviewVersion.VersionNumber && (
               <div className="go-to-latest-version-button">
                  <ControlButton buttonType={TscButtonType.destructive}
                     label={Strings.labels.versions.goToLatestButtonText}
                     onClick={this.goToLatestVersion}
                     testId="go-to-latest-version-button" />
               </div>
            )}
         </>
      );

      const headerRight =
      <>
         {hasOwnerPermissions && <div className="share-review-button">
            <ShareReviewButtonContainer />
         </div>}
         {currentUser.userType && currentUser.userType !== UserTypes.selfIdentified && <NotificationsClientPluginWrapperContainer />}
         <SettingsAndAccountWidget showSigninButton={true} />
      </>;

      return (
         <div className={reviewContainerClassName} id="review-container">
            <div className="accessibility-container">
               <div className="bypass-block-container">
                  <a href="#start-of-content" onClick={() => setSelectedComment('')} className="bypass-block button-style" tabIndex={0}>{Strings.accessibility.reviewBypassBlockText}</a>
               </div>
               <span className="screen-reader-only" id="aria-live-status" role="status" aria-live="polite" />
            </div>
            <Header leftSection={headerLeft} rightSection={headerRight} />
            <div className="review">
               <div className="video-and-feedback" id="start-of-content">
                  <ToolbarContainer />
                  {!versionLoading ? <>
                     {isVideo && <SmartplayerReviewVideoContainer annotationMode={annotationMode} storeAnnotation={this.storeAnnotation} />}
                     {!isVideo && <ImageContainer storeAnnotation={this.storeAnnotation} allowAnnotation={true} allowZoom={true} />}
                     <FeedbackContainer goToLatest={this.goToLatestVersion} maxRows={Constants.textAreaMaxRows.feedback} addComment={this.addComment} localStorageKey={this.addCommentLocalStorageKey} showAnnotationsBar={true} showCommentingAs={true} annotationExists={newAnnotation.length !== 0} />
                  </> : <LoadingSpinner />
                  }
               </div>
               <ConversationContainer reviewId={review.Id} showCommentTime={isVideo} hideEmptyState={versionLoading} />
            </div>
            {isCommentHotkeyEnabled && <Hotkey character="z" callback={this.addSilentComment} />}
            {showAlert && <AlertModal title={Strings.labels.versions.deleteVersion}
               onClose={this.onCancelDeletion}
               closeContent={Strings.labels.cancel}
               onConfirm={this.onPerformDeletion}
               confirmContent={Strings.labels.delete}
               confirmButtonType={TscButtonType.destructive}
               onDismiss={this.onCancelDeletion}
               dismissContent={Strings.labels.cancel}
               hideButtons={deletingVersion}
               testId="delete-version-alert">
               {alertContent}
            </AlertModal>}
         </div>
      );
   }
}

export interface ReviewProps extends RouteComponentProps, Themeable {
   review: ReviewModel;
   reviewVersion: ReviewVersionModel;
   player: IVideoPlayer;
   modalState: ModalStates;
   currentTime: number;
   annotationMode: AnnotationModes;
   currentUser: UserModel;
   isCommentHotkeyEnabled: boolean;
   versionLoading: boolean;
   hasOwnerPermissions: boolean;
   setSelectedComment(commentId: string): void;
   setAnnotationMode(annotationMode: AnnotationModes): void;
   setModalState(modalState: ModalStates): void;
}

export interface ReviewState {
   newAnnotation: string;
   deletingVersion: boolean;
   showAlert: boolean;
   versionToDelete: ReviewVersionModel;
}
